import axios, {
  type AxiosInstance,
  type AxiosRequestHeaders,
  type AxiosResponse,
} from "axios";
import React, {
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { storageAccountKeys } from "@repo/types/queries";
import type {
  AccountLoginResponse,
  ExecutionResponse,
} from "@repo/types/responses";
import { ApiAccessContext } from "@repo/contexts";
import type { CustomAxiosRequestConfig, ApiAccessConfig } from "@repo/configs";
import { useAuth } from "@repo/hooks/contexts";

export function ApiAccessProvider({
  children,
  config,
}: {
  children: React.ReactNode;
  config: ApiAccessConfig;
}) {
  const { user: userPrincipal } = useAuth();
  const staticLocalDevToken = config.localDevAccessToken?.trim() || null;
  const isStaticLocalDevTokenEnabled = !!staticLocalDevToken;
  const accessTokenRef = useRef<string | null>(staticLocalDevToken);
  const [accessToken, setAccessTokenState] = useState<string | null>(
    staticLocalDevToken,
  );
  const isRefreshing = useRef(false);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const requestQueue = useRef<((token: string | null) => void)[]>([]);

  const apiClient: AxiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: config.baseURL,
        withCredentials: config.withCredentials ?? true, // important for refresh via cookies
      }),
    [config.baseURL, config.withCredentials],
  );

  // --- Set Access Token Method ---
  const setAccessToken = useCallback((token: string | null) => {
    accessTokenRef.current = token;
    setAccessTokenState(token);
  }, []);

  // --- Helper: Refresh Token ---
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!refreshPromiseRef.current) {
      isRefreshing.current = true;

      const sessionId = userPrincipal?.SessionId;

      refreshPromiseRef.current = (async () => {
        try {
          const paras = {
            sessionId,
            platform: config.platform,
            refreshToken: config.getRefreshToken
              ? await config.getRefreshToken()
              : undefined,
          };

          //  Default (web cookie-based)
          const res = await axios.post<AccountLoginResponse>(
            `${config.refreshEndpoint}`,
            { ...paras },
            {
              withCredentials: config.withCredentials ?? true,
              headers: { "Content-Type": "application/json" },
            },
          );
          console.log("REFRESH RESPONSE =>", JSON.stringify(res.data, null, 2));
          if (res.data.Result && res.data.Token) {
            accessTokenRef.current = res.data.Token;
            setAccessTokenState(res.data.Token);
            if (config.onRefreshTokenSuccess) {
              await config.onRefreshTokenSuccess(res.data.RefreshToken);
            }
            return res.data.Token;
          }

          throw new Error("Invalid refresh response");
        } catch (err) {
          console.error("Refresh failed", err);
          accessTokenRef.current = null;
          setAccessTokenState(null);
          return null;
        } finally {
          isRefreshing.current = false;
          refreshPromiseRef.current = null;
        }
      })();
    }

    return refreshPromiseRef.current;
  }, [
    userPrincipal,
    config.baseURL,
    config.refreshEndpoint,
    config.withCredentials,
    config.platform,
  ]);

  useEffect(() => {
    if (staticLocalDevToken) {
      setAccessToken(staticLocalDevToken);
    }
  }, [setAccessToken, staticLocalDevToken]);

  useEffect(() => {
    // --- Request Interceptor ---
    const reqInterceptor = apiClient.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        if (!config.skipAuth && accessTokenRef.current) {
          if (!config.headers) config.headers = {} as AxiosRequestHeaders;
          config.headers["Authorization"] = `Bearer ${accessTokenRef.current}`;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // --- Response Interceptor ---
    const resInterceptor = apiClient.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isStaticLocalDevTokenEnabled) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          if (isRefreshing.current) {
            // Wait for ongoing refresh
            return new Promise((resolve, reject) => {
              requestQueue.current.push((token) => {
                if (!token) return reject(error);

                if (!originalRequest.headers) {
                  originalRequest.headers = {} as AxiosRequestHeaders;
                }

                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(apiClient(originalRequest));
              });
            });
          }

          try {
            const newToken = await refreshAccessToken();

            if (newToken) {
              requestQueue.current.forEach((cb) => cb(newToken));
              requestQueue.current = [];

              if (!originalRequest.headers) {
                originalRequest.headers = {} as AxiosRequestHeaders;
              }

              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }

            throw new Error("No token after refresh");
          } catch (refreshError) {
            requestQueue.current.forEach((cb) => cb(null));
            requestQueue.current = [];
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.request.eject(reqInterceptor);
      apiClient.interceptors.response.eject(resInterceptor);
    };
  }, [refreshAccessToken, apiClient, isStaticLocalDevTokenEnabled]);

  const { data: storageContainerSas } = useQuery({
    queryKey: storageAccountKeys.details(1),
    queryFn: async () => {
      const response =
        await apiClient.get<ExecutionResponse>(`/fileAsset/GetSas/`);

      if (!response.data.Result || !response.data.Para1) {
        throw new Error("Failed to fetch storage container SAS");
      }

      return response.data.Para1;
    },
    staleTime: Infinity,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchIntervalInBackground: true,
    enabled: !!accessToken, // Only fetch if we have an access token
  });

  return (
    <ApiAccessContext.Provider
      value={{
        accessToken: accessTokenRef.current,
        setAccessToken,
        apiClient,
        storageContainerSas,
      }}
    >
      {children}
    </ApiAccessContext.Provider>
  );
}
